<script setup>
// NOTICE: KAPTION-INTEGRATION
// template-languages.SETTINGS.1..3 — which utility-template languages agents are
// offered in the reply box. Settings → Account is administrator-gated, so this
// control is admin-only by construction. At least one language must stay on.
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import {
  SUPPORTED_TEMPLATE_LANGUAGES,
  allowedTemplateLanguages,
} from 'dashboard/helper/kaptionTemplateLanguages';
import KaptionCircleFlag from 'dashboard/components-next/flag/KaptionCircleFlag.vue';
import SectionLayout from './SectionLayout.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const LANG_NAMES = {
  es: 'Español',
  en: 'English',
  pt_BR: 'Português',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
};

const selected = ref(allowedTemplateLanguages(currentAccount.value));

watch(
  currentAccount,
  () => {
    selected.value = allowedTemplateLanguages(currentAccount.value);
  },
  { deep: true }
);

const isOnlyOne = lang =>
  selected.value.length === 1 && selected.value.includes(lang);

const toggle = async lang => {
  if (isOnlyOne(lang)) {
    useAlert(t('KAPTION_TEMPLATE_LANGUAGES.MIN_ONE'));
    return;
  }
  const previous = selected.value;
  const next = selected.value.includes(lang)
    ? selected.value.filter(code => code !== lang)
    : [...selected.value, lang];
  selected.value = SUPPORTED_TEMPLATE_LANGUAGES.filter(code =>
    next.includes(code)
  );
  try {
    await updateAccount({ kaption_template_languages: selected.value });
    useAlert(t('KAPTION_TEMPLATE_LANGUAGES.API.SUCCESS'));
  } catch (error) {
    selected.value = previous;
    useAlert(t('KAPTION_TEMPLATE_LANGUAGES.API.ERROR'));
  }
};
</script>

<template>
  <SectionLayout
    :title="t('KAPTION_TEMPLATE_LANGUAGES.TITLE')"
    :description="t('KAPTION_TEMPLATE_LANGUAGES.NOTE')"
    with-border
    data-testid="kaption-template-languages-setting"
  >
    <div
      role="group"
      :aria-label="t('KAPTION_TEMPLATE_LANGUAGES.LABEL')"
      class="flex max-w-md flex-wrap gap-2"
    >
      <button
        v-for="lang in SUPPORTED_TEMPLATE_LANGUAGES"
        :key="lang"
        type="button"
        role="checkbox"
        :aria-checked="selected.includes(lang)"
        :aria-disabled="isOnlyOne(lang)"
        :data-testid="`template-language-${lang}`"
        class="flex h-8 items-center gap-2 rounded-lg border px-3 text-sm"
        :class="
          selected.includes(lang)
            ? 'border-n-brand bg-n-alpha-2 font-medium text-n-slate-12'
            : 'border-n-weak text-n-slate-11 hover:text-n-slate-12'
        "
        @click="toggle(lang)"
      >
        <KaptionCircleFlag
          :language-code="lang.replace('_BR', '')"
          size="size-4"
        />
        {{ LANG_NAMES[lang] }}
      </button>
    </div>
  </SectionLayout>
</template>
